import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { BlockOutlined, ContentCopy, LinkOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { invitesService, rbacService } from "../../services";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "PENDING", label: "Pendiente" },
  { value: "USED", label: "Usada" },
  { value: "EXPIRED", label: "Expirada" },
  { value: "CANCELED", label: "Cancelada" },
];

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const normalizeCompany = (source) => {
  const item = source?.company || source?.branch || source;
  if (!item || typeof item !== "object") return null;
  return {
    id: item.id,
    name: item.name || item.alias || item.legalName || `Sucursal ${item.id}`,
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

const getStatusMeta = (status) => {
  if (status === "PENDING") return { color: "warning", label: "Pendiente" };
  if (status === "USED") return { color: "success", label: "Usada" };
  if (status === "CANCELED") return { color: "error", label: "Cancelada" };
  return { color: "default", label: "Expirada" };
};

function InvitesListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [branches, setBranches] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [cancelDialog, setCancelDialog] = useState({ open: false, invite: null });
  const [canceling, setCanceling] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [invitesData, companiesData] = await Promise.all([
        invitesService.list(),
        rbacService.listCompanies(),
      ]);
      setInvites(toArray(invitesData));
      setBranches(toArray(companiesData).map(normalizeCompany).filter(Boolean));
    } catch {
      setInvites([]);
      setBranches([]);
      showSnack("No se pudieron cargar las invitaciones.", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnack]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRows = useMemo(() => {
    return invites.filter((invite) => {
      if (statusFilter && invite?.status !== statusFilter) return false;
      if (branchFilter) {
        const hasBranch = toArray(invite?.branches).some(
          (row) => String(row?.branch?.id || "") === String(branchFilter)
        );
        if (!hasBranch) return false;
      }
      return true;
    });
  }, [branchFilter, invites, statusFilter]);

  const handleCopy = useCallback(async (invite) => {
    const link = String(invite?.invite_link || "").trim();
    if (!link) {
      showSnack("Esta invitacion no tiene un link recuperable.", "warning");
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      showSnack("Link copiado", "success");
    } catch {
      showSnack("No se pudo copiar el link.", "error");
    }
  }, [showSnack]);

  const handleCancelInvite = useCallback(async () => {
    const invite = cancelDialog.invite;
    if (!invite?.id) return;
    setCanceling(true);
    try {
      await invitesService.cancel(invite.id);
      setCancelDialog({ open: false, invite: null });
      showSnack("Invitacion cancelada.", "success");
      await loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo cancelar la invitacion.";
      showSnack(detail, "error");
    } finally {
      setCanceling(false);
    }
  }, [cancelDialog.invite, loadData, showSnack]);

  const columns = useMemo(
    () => [
      {
        field: "branch_name",
        headerName: "Sucursal",
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) => row?.branches?.[0]?.branch?.name || "-",
      },
      {
        field: "role_name",
        headerName: "Rol",
        flex: 1,
        minWidth: 170,
        valueGetter: (_, row) => row?.branches?.[0]?.role?.name || "Sin rol",
      },
      {
        field: "expires_at",
        headerName: "Expira",
        minWidth: 165,
        flex: 1,
        valueGetter: (_, row) => formatDateTime(row?.expires_at),
      },
      {
        field: "status",
        headerName: "Estado",
        minWidth: 130,
        renderCell: ({ row }) => {
          const meta = getStatusMeta(row?.status);
          return <Chip size="small" color={meta.color} label={meta.label} />;
        },
      },
      {
        field: "created_at",
        headerName: "Generado",
        minWidth: 165,
        flex: 1,
        valueGetter: (_, row) => formatDateTime(row?.created_at),
      },
      {
        field: "actions",
        headerName: "Acciones",
        minWidth: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Copiar link">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(row)}
                  disabled={row?.status !== "PENDING"}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Cancelar link">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setCancelDialog({ open: true, invite: row })}
                  disabled={row?.status !== "PENDING"}
                >
                  <BlockOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleCopy]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 1.5,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={{ "& .MuiTypography-h4, & .MuiTypography-h5": { fontWeight: 500 } }}>
          <MainText
            icon={LinkOutlined}
            title="Invitaciones"
            subtitle="Links de acceso de un solo uso"
          />
        </Box>
        <Button variant="contained" onClick={() => navigate("/admin/invites/create")}>
          Generar invitacion
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
          <FormControl size="small" sx={{ width: { xs: "100%", md: 140 } }}>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value || "all"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: { xs: "100%", md: 180 } }}>
            <InputLabel>Sucursal</InputLabel>
            <Select
              label="Sucursal"
              value={branchFilter}
              onChange={(event) => setBranchFilter(event.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={String(branch.id)}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ height: "calc(100vh - 290px)", minHeight: 360 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#757B2B",
                color: "#ffffff",
              },
              "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover":
              {
                backgroundColor: "#757B2B !important",
              },
              "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
              {
                color: "#ffffff !important",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "rgba(16, 23, 60, 0.08)",
              },
            }}
        />
      </Paper>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />

      <Dialog
        open={cancelDialog.open}
        onClose={() => {
          if (canceling) return;
          setCancelDialog({ open: false, invite: null });
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cancelar invitacion</DialogTitle>
        <DialogContent dividers>
          Vas a invalidar este link. Ya no se podra usar para crear una cuenta.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialog({ open: false, invite: null })}
            disabled={canceling}
          >
            Cerrar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleCancelInvite}
            disabled={canceling}
          >
            {canceling ? "Cancelando..." : "Cancelar link"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InvitesListPage;
